import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast, Icon } from '@tviklax/ui-components';
import { Layout } from './shared/layouts/Layout';
import { Header } from './shared/layouts/Header';
import { Dashboard } from './features/dashboard/components';
import { CreateOrderPage } from './pages/CreateOrderPage';
import { ExceptionQueuePage } from './pages/ExceptionQueuePage';
import { Scheduling } from './features/scheduling/components';
import { Payments } from './features/payments/components';
import { ShipmentsPage } from './features/shipments/components';
import { Tracking } from './features/tracking/components';
import { PaymentHistory } from './features/payment-history/components';
import { ProfileSettings } from './features/profile-settings';
import { ChangePassword } from './features/change-password';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { HelpCenterPage } from './pages/HelpCenterPage';
import { BusinessSettings } from './features/business-settings';
import { SellerRegistrationPage } from './pages/SellerRegistrationPage';
import { CustomsDeclarationPage } from './pages/CustomsDeclarationPage';
import DocumentsPage from './pages/DocumentsPage';
import ContactsPage from './pages/ContactsPage';
import { ReconciliationOverview } from './features/finance-reconciliation/components/ReconciliationOverview';
import { ProductionStatusPage } from './pages/ProductionStatusPage';
import { ForexCompliancePage } from './pages/ForexCompliancePage';
import { MessagingPlatform } from './features/messaging/components/MessagingPlatform';
import { CountryProvider } from './context/CountryProvider';
import { PreferencesProvider } from './context/PreferencesContext';
import { RoleProvider } from './context/RoleContext';
import { useUser } from './context/UserContext';
import { MessagingConversation, MessagingLaunchPayload, MessagingSurface, resolveMessagingConversation } from './features/messaging/utils/messagingApi';

const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 64px 0 0 0;
  background: rgba(15, 23, 42, 0.18);
  z-index: 210;
`;

const DrawerPanel = styled.div`
  position: fixed;
  top: 76px;
  right: 16px;
  bottom: 16px;
  width: min(520px, calc(100vw - 24px));
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(18px);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
  padding: 16px;
  z-index: 220;
`;

const WidgetRail = styled.div`
  position: fixed;
  right: 18px;
  bottom: 18px;
  display: flex;
  flex-direction: row-reverse;
  gap: 12px;
  z-index: 230;
  align-items: flex-end;
`;

const WidgetBubbleWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const WidgetBubble = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  box-shadow: 0 18px 30px rgba(37, 99, 235, 0.18);
  color: #1e3a8a;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const WidgetUnreadBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 14px rgba(239, 68, 68, 0.28);
`;

const WidgetPresenceDot = styled.span<{ $live: boolean }>`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid white;
  background: ${({ $live }) => ($live ? '#22c55e' : '#f59e0b')};
`;

const WidgetSoundIndicator = styled.span<{ $enabled: boolean }>`
  position: absolute;
  left: -4px;
  bottom: -6px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${({ $enabled }) => ($enabled ? '#0f172a' : '#94a3b8')};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.18);
`;

const HiddenWidgetHost = styled.div<{ $open: boolean; $index: number }>`
  position: fixed;
  right: ${({ $index }) => 24 + $index * 408}px;
  bottom: 78px;
  width: 396px;
  height: min(560px, calc(100vh - 96px));
  z-index: ${({ $open }) => ($open ? 180 : -1)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const WidgetWindow = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 54px rgba(15, 23, 42, 0.22);
  border: 1px solid #dbeafe;
  overflow: hidden;
`;

const MessagingPopoutShell = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #f3f2f1;
`;

const MessagingPopoutBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 16px 16px;
  overflow: hidden;
`;

type FloatingWidgetState = MessagingConversation & {
  id: string;
  open: boolean;
  unreadCount: number;
  live: boolean;
  soundEnabled: boolean;
  lastMessageId?: number | string;
};

const defaultMessagingContext: MessagingConversation = {
  threadId: 7,
  threadTitle: 'Order ORD-001 Communication',
  targetContactId: 1,
  targetContactName: 'Dr. Ravi Kapoor',
};

export type TabId =
  | 'dashboard'
  | 'create-order'
  | 'forex-compliance'
  | 'production'
  | 'scheduling'
  | 'payments'
  | 'reconciliation'
  | 'shipments'
  | 'tracking'
  | 'documents'
  | 'messaging'
  | 'contacts'
  | 'payment-history'
  | 'profile-settings'
  | 'change-password'
  | 'business-settings'
  | 'customs'
  | 'exceptions';

type ViewState = 'landing' | 'login' | 'dashboard' | 'seller-registration' | 'privacy' | 'terms' | 'help';

const playWidgetNotificationTone = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return;
  }

  const audioContext = new AudioContextCtor();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.value = 880;
  gain.gain.value = 0.03;
  oscillator.connect(gain);
  gain.connect(audioContext.destination);
  oscillator.start();
  oscillator.stop(audioContext.currentTime + 0.16);
  oscillator.onended = () => {
    void audioContext.close();
  };
};

function App() {
  const searchParams = useMemo(
    () => (typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)),
    []
  );
  const isMessagingPopout = searchParams.get('messagingPopout') === '1';
  const popoutThreadId = Number(searchParams.get('threadId') || defaultMessagingContext.threadId);
  const popoutThreadTitle = searchParams.get('threadTitle') || defaultMessagingContext.threadTitle;
  const popoutTargetContactId = searchParams.get('targetContactId');
  const popoutTargetContactName = searchParams.get('targetContactName') || undefined;

  const [currentView, setCurrentView] = useState<ViewState>(isMessagingPopout ? 'login' : 'landing');
  const [legalReturnView, setLegalReturnView] = useState<ViewState>('landing');
  const openLegalPage = (page: 'privacy' | 'terms' | 'help') => {
    setLegalReturnView(currentView);
    setCurrentView(page);
  };
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const [pendingNavigation, setPendingNavigation] = useState<TabId | null>(null);
  const [selectedPaymentOrderId, setSelectedPaymentOrderId] = useState<string | undefined>(undefined);
  const [autoOpenPayModal, setAutoOpenPayModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | undefined>(undefined);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(undefined);
  const [messagingContext, setMessagingContext] = useState<MessagingConversation>(defaultMessagingContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatWidgets, setChatWidgets] = useState<FloatingWidgetState[]>([]);
  const [customsContext, setCustomsContext] = useState({
    orderId: '',
    carrier: '',
    vessel: '',
    eta: '',
  });
  const { user, setUser, logout, isLoggedIn } = useUser();
  const senderContactId = Number(user?.contactId || 0);
  const senderName = user?.name || 'User';
  const preferredLanguage = user?.preferredLanguage || 'en';

  const openMessagingInNewWindow = (conversation: MessagingConversation) => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams();
    params.set('messagingPopout', '1');
    params.set('threadId', String(conversation.threadId));
    params.set('threadTitle', conversation.threadTitle || 'Messaging');
    if (conversation.targetContactId !== undefined) {
      params.set('targetContactId', String(conversation.targetContactId));
    }
    if (conversation.targetContactName) {
      params.set('targetContactName', conversation.targetContactName);
    }

    window.open(
      `${window.location.pathname}?${params.toString()}`,
      '_blank',
      'noopener,noreferrer,width=1100,height=780'
    );
  };

  const upsertWidget = (conversation: MessagingConversation, open: boolean) => {
    const widgetId = String(conversation.threadId);
    setChatWidgets((prev) => {
      const existing = prev.find((item) => item.id === widgetId);
      if (existing) {
        const updated = { ...existing, open, unreadCount: open ? 0 : existing.unreadCount };
        if (conversation.threadTitle !== undefined) updated.threadTitle = conversation.threadTitle;
        if (conversation.targetContactName !== undefined) updated.targetContactName = conversation.targetContactName;
        if (conversation.targetContactId !== undefined) updated.targetContactId = conversation.targetContactId;
        return prev.map((item) => item.id === widgetId ? updated : item);
      }
      return [{ id: widgetId, ...conversation, open, unreadCount: 0, live: false, soundEnabled: true }, ...prev].slice(0, 3);
    });
  };

  const handleWidgetMessageActivity = ({
    threadId,
    messageId,
    own,
    direct,
  }: {
    threadId: number;
    messageId?: number | string;
    own: boolean;
    direct: boolean;
  }) => {
    if (own) {
      return;
    }

    let shouldPlayTone = false;
    setChatWidgets((prev) => prev.map((item) => {
      if (item.threadId !== threadId) {
        return item;
      }
      if (item.lastMessageId === messageId) {
        return item;
      }
      if (!item.open && item.soundEnabled) {
        shouldPlayTone = true;
      }
      return {
        ...item,
        open: direct ? true : item.open,
        lastMessageId: messageId,
        unreadCount: (direct || item.open) ? 0 : item.unreadCount + 1,
      };
    }));

    if (shouldPlayTone) {
      playWidgetNotificationTone();
    }
  };

  const handleWidgetConnectionStatus = ({ threadId, connected }: { threadId: number; connected: boolean }) => {
    setChatWidgets((prev) => prev.map((item) => item.threadId === threadId ? { ...item, live: connected } : item));
  };

  const openMessaging = async (payload: MessagingLaunchPayload = {}, surface: MessagingSurface = payload.preferredSurface || 'widget') => {
    try {
      const conversation = await resolveMessagingConversation({
        ...payload,
        senderFallbackContactId: senderContactId,
        senderName,
        senderEmail: user?.email,
        preferredLanguage,
      });
      setMessagingContext(conversation);
      const normalizedSurface: MessagingSurface = surface === 'drawer' ? 'widget' : surface;

      if (normalizedSurface === 'tab') {
        setActiveTab('messaging');
        setDrawerOpen(false);
        return;
      }

      if (normalizedSurface === 'widget') {
        upsertWidget(conversation, true);
        setDrawerOpen(false);
        return;
      }

      setDrawerOpen(true);
    } catch (error) {
      toast({
        title: 'Messaging unavailable',
        description: 'Could not open the conversation right now.',
        variant: 'destructive',
      });
    }
  };

  const minimizeConversation = (conversation: MessagingConversation) => {
    upsertWidget(conversation, false);
    setDrawerOpen(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard
          onViewOperationsHub={() => setActiveTab('shipments')}
          onOrderClick={(orderId) => {
            setSelectedOrderId(orderId);
            setActiveTab('shipments');
          }}
          onCreateOrder={() => setActiveTab('create-order')}
          onBrowseMarketplace={() => {
            if (typeof window !== 'undefined') {
              window.open('https://test.tvikla.com', '_blank', 'noopener,noreferrer');
            }
          }}
          onOpenPayments={(orderId) => {
            setSelectedOrderId(orderId);
            setAutoOpenPayModal(true);
            setActiveTab('payments');
          }}
          onOpenDocuments={(orderId, documentId) => {
            setSelectedOrderId(orderId);
            setSelectedDocumentId(documentId);
            setActiveTab('documents');
          }}
          onOpenMessaging={({ threadId, subject, orderId, targetContactId, targetContactName, targetContactEmail, draftText }) => {
            openMessaging({
              threadId,
              threadTitle: subject,
              orderId,
              targetContactId,
              targetContactName: targetContactName ?? undefined,
              targetContactEmail: targetContactEmail ?? undefined,
              draftText,
              preferredSurface: 'drawer',
            }, 'drawer');
          }}
          onTrackShipment={({ orderId, shipmentNo }) => {
            setSelectedShipmentId(shipmentNo || orderId);
            setSelectedOrderId(orderId);
            setActiveTab('shipments');
          }}
        />;
      case 'create-order':
        return <CreateOrderPage />;
      case 'exceptions':
        return <ExceptionQueuePage />;
      case 'forex-compliance':
        return <ForexCompliancePage initialOrderId={selectedOrderId || selectedPaymentOrderId} />;
      case 'production':
        return <ProductionStatusPage />;
      case 'scheduling':
        return (
          <Scheduling
            selectedOrderId={selectedOrderId}
            onOrderChange={setSelectedOrderId}
            onNavigateToCustoms={(payload) => {
              if (payload) setCustomsContext(payload);
              setActiveTab('customs');
            }}
          />
        );
      case 'payments':
        return (
          <Payments
            initialOrderId={selectedOrderId || selectedPaymentOrderId}
            autoOpenPay={autoOpenPayModal}
            onAutoOpenPayHandled={() => setAutoOpenPayModal(false)}
            onOrderSelect={(orderId) => {
              setSelectedOrderId(orderId);
            }}
            onOpenMessaging={({ orderId, contactName, contactEmail }) => {
              openMessaging({
                orderId,
                threadTitle: contactName ? `Order ${orderId} - ${contactName}` : `Order ${orderId} Communication`,
                targetContactName: contactName,
                targetContactEmail: contactEmail,
                preferredSurface: 'drawer',
              }, 'drawer');
            }}
            onPaymentSuccess={(orderId, message) => {
              setSelectedPaymentOrderId(orderId);
              setSelectedOrderId(orderId);
              toast({ title: 'Payment updated', description: message, variant: 'success' });
            }}
          />
        );
      case 'messaging':
        {
          const senderName = user?.name || 'User';
        return (
          <MessagingPlatform
            initialThreadId={messagingContext.threadId}
            initialThreadTitle={messagingContext.threadTitle}
            senderContactId={senderContactId}
            senderName={senderName}
            senderEmail={user?.email}
            initialTargetContactId={messagingContext.targetContactId}
            initialTargetContactName={messagingContext.targetContactName}
            initialDraftText={messagingContext.draftText}
            preferredLanguage={preferredLanguage}
            mode="page"
            onMinimizeConversation={minimizeConversation}
            onConversationChange={(conversation) => setMessagingContext((prev) => ({ ...prev, ...conversation }))}
            onPopoutConversation={openMessagingInNewWindow}
          />
        );
        }
      case 'contacts':
        return <ContactsPage />;
      case 'reconciliation':
        return (
          <ReconciliationOverview
            highlightOrderId={selectedPaymentOrderId || selectedOrderId}
            onOpenOrder={(orderId: string) => {
              setSelectedOrderId(orderId);
              setActiveTab('payments');
            }}
            onOpenComplianceOrder={(orderId: string) => {
              setSelectedOrderId(orderId);
              setActiveTab('forex-compliance');
            }}
            onOpenShipment={(shipmentId: string) => {
              setSelectedShipmentId(shipmentId);
              setActiveTab('shipments');
            }}
          />
        );
      case 'shipments':
        return (
          <ShipmentsPage
            shipmentId={selectedShipmentId || selectedOrderId}
            selectedOrderId={selectedOrderId}
            onOrderChange={setSelectedOrderId}
            onNavigateToCustoms={() => {
              setActiveTab('customs');
            }}
            onOpenMessaging={({ orderId, targetContactId, targetContactName, targetContactEmail, preferredSurface }) => {
              openMessaging({ orderId, targetContactId, targetContactName, targetContactEmail, preferredSurface: preferredSurface || 'drawer' }, preferredSurface || 'drawer');
            }}
          />
        );
      case 'tracking':
        return <Tracking />;
      case 'documents':
        return (
          <DocumentsPage
            onOpenOrder={(orderId: string) => {
              setSelectedOrderId(orderId);
              setActiveTab('payments');
            }}
            onOpenMessaging={({ targetContactName, targetContactEmail, preferredSurface }) => {
              openMessaging({
                targetContactName,
                targetContactEmail,
                preferredSurface: preferredSurface || 'drawer',
              }, preferredSurface || 'drawer');
            }}
            initialDocumentId={selectedDocumentId}
          />
        );
      case 'payment-history':
        return <PaymentHistory />;
      case 'profile-settings':
        return <ProfileSettings />;
      case 'change-password':
        return <ChangePassword />;
      case 'business-settings':
        return <BusinessSettings />;
      case 'customs':
        return (
          <CustomsDeclarationPage
            orderId={customsContext.orderId}
            carrier={customsContext.carrier}
            vessel={customsContext.vessel}
            eta={customsContext.eta}
            selectedOrderId={selectedOrderId}
            onOrderChange={setSelectedOrderId}
            onBack={() => setActiveTab('scheduling')}
            onOpenMessaging={({ orderId, targetContactId, targetContactName, targetContactEmail, preferredSurface }) => {
              openMessaging({ orderId, targetContactId, targetContactName, targetContactEmail, preferredSurface: preferredSurface || 'drawer' }, preferredSurface || 'drawer');
            }}
          />
        );
      default:
        return <Dashboard />;
    }
  };

  if (currentView === 'landing') {
    return (
      <LandingPage
        onLogin={() => setCurrentView('login')}
        onRegister={() => setCurrentView('seller-registration')}
        onNavigateToCreateOrder={() => {
          if (!isLoggedIn) {
            setPendingNavigation('create-order');
            setCurrentView('login');
          } else {
            setCurrentView('dashboard');
            setActiveTab('create-order');
          }
        }}
        onNavigateToMarketplace={() => {
          if (typeof window !== 'undefined') {
            window.open('https://test.tvikla.com', '_blank', 'noopener,noreferrer');
          }
        }}
        onNavigateToPayments={() => {
          if (!isLoggedIn) {
            setPendingNavigation('payments');
            setCurrentView('login');
          } else {
            setCurrentView('dashboard');
            setActiveTab('payments');
          }
        }}
        onNavigateToPrivacy={() => openLegalPage('privacy')}
        onNavigateToTerms={() => openLegalPage('terms')}
        onNavigateToHelp={() => openLegalPage('help')}
      />
    );
  }

  if (currentView === 'privacy') {
    return <PrivacyPolicyPage onBack={() => setCurrentView(legalReturnView)} />;
  }

  if (currentView === 'terms') {
    return <TermsOfServicePage onBack={() => setCurrentView(legalReturnView)} />;
  }

  if (currentView === 'help') {
    return <HelpCenterPage onBack={() => setCurrentView(legalReturnView)} />;
  }

  if (currentView === 'login') {
    return (
      <LoginPage
        onLogin={(userData) => {
          setUser(userData);
          if (pendingNavigation) {
            setActiveTab(pendingNavigation);
            setPendingNavigation(null);
          }
          setCurrentView('dashboard');
        }}
        onSignUp={() => setCurrentView('seller-registration')}
      />
    );
  }

  if (currentView === 'seller-registration') {
    return (
      <PreferencesProvider>
        <CountryProvider>
          <SellerRegistrationPage />
        </CountryProvider>
      </PreferencesProvider>
    );
  }

  if (isMessagingPopout && isLoggedIn) {
    return (
      <PreferencesProvider>
        <RoleProvider>
          <MessagingPopoutShell>
            <Header
              onMenuToggle={() => {}}
              onLogout={() => { logout(); setCurrentView('landing'); }}
              onNavigate={() => {}}
              userName={senderName}
              userRole={user?.role}
            />
            <MessagingPopoutBody>
              <MessagingPlatform
                initialThreadId={Number.isNaN(popoutThreadId) ? defaultMessagingContext.threadId : popoutThreadId}
                initialThreadTitle={popoutThreadTitle}
                senderContactId={senderContactId}
                senderName={senderName}
                senderEmail={user?.email}
                initialTargetContactId={popoutTargetContactId ? Number(popoutTargetContactId) : undefined}
                initialTargetContactName={popoutTargetContactName}
                preferredLanguage={preferredLanguage}
                mode="page"
                onMinimizeConversation={minimizeConversation}
                onConversationChange={(conversation) => setMessagingContext((prev) => ({ ...prev, ...conversation }))}
                onPopoutConversation={openMessagingInNewWindow}
              />
            </MessagingPopoutBody>
          </MessagingPopoutShell>
        </RoleProvider>
      </PreferencesProvider>
    );
  }

  return (
    <PreferencesProvider>
      <RoleProvider>
        <Layout
          activeTab={activeTab}
          setActiveTab={(tab) => setActiveTab(tab as TabId)}
          onOpenMessagingDrawer={() => {
            if (drawerOpen) {
              setDrawerOpen(false);
              return;
            }
            openMessaging(messagingContext, 'widget');
          }}
          onLogout={() => {
            logout();
            setCurrentView('landing');
          }}
          onNavigateLegal={openLegalPage}
          userName={user?.name || 'User'}
          userRole={user?.role}
        >
          {renderContent()}
        </Layout>
        {drawerOpen && (
          <>
            <DrawerBackdrop onClick={() => setDrawerOpen(false)} />
            <DrawerPanel>
              <MessagingPlatform
                key="drawer-messaging"
                initialThreadId={messagingContext.threadId}
                initialThreadTitle={messagingContext.threadTitle}
                senderContactId={senderContactId}
                senderName={senderName}
                senderEmail={user?.email}
                initialTargetContactId={messagingContext.targetContactId}
                initialTargetContactName={messagingContext.targetContactName}
                initialDraftText={messagingContext.draftText}
                preferredLanguage={preferredLanguage}
                mode="drawer"
                onMinimizeConversation={minimizeConversation}
                onCloseConversation={() => setDrawerOpen(false)}
                onPopoutConversation={openMessagingInNewWindow}
                onOpenHub={() => {
                  setActiveTab('messaging');
                  setDrawerOpen(false);
                }}
                onConversationChange={(conversation) => setMessagingContext((prev) => ({ ...prev, ...conversation }))}
              />
            </DrawerPanel>
          </>
        )}
        {chatWidgets.length > 0 && (
          <>
            {chatWidgets.map((widget, index) => (
              <HiddenWidgetHost key={`host-${widget.id}`} $open={widget.open} $index={index}>
                <WidgetWindow>
                  <MessagingPlatform
                    key={`widget-${widget.id}`}
                    initialThreadId={widget.threadId}
                    initialThreadTitle={widget.threadTitle}
                    senderContactId={senderContactId}
                    senderName={senderName}
                    senderEmail={user?.email}
                    initialTargetContactId={widget.targetContactId}
                    initialTargetContactName={widget.targetContactName}
                    initialDraftText={widget.draftText}
                    preferredLanguage={preferredLanguage}
                    mode="widget"
                    onMinimizeConversation={(conversation) => upsertWidget(conversation, false)}
                    onCloseConversation={() => setChatWidgets((prev) => prev.filter((item) => item.id !== widget.id))}
                    onPopoutConversation={openMessagingInNewWindow}
                    onOpenHub={() => {
                      setMessagingContext(widget);
                      setActiveTab('messaging');
                      setChatWidgets((prev) => prev.map((item) => item.id === widget.id ? { ...item, unreadCount: 0 } : item));
                    }}
                    onConversationChange={(conversation) => {
                      setChatWidgets((prev) => prev.map((item) => item.id === widget.id ? { ...item, ...conversation } : item));
                    }}
                    onMessageActivity={handleWidgetMessageActivity}
                    onConnectionStatusChange={handleWidgetConnectionStatus}
                  />
                </WidgetWindow>
              </HiddenWidgetHost>
            ))}
            <WidgetRail>
              {chatWidgets.filter((widget) => !widget.open).map((widget) => (
                <WidgetBubbleWrap key={widget.id}>
                  <WidgetBubble
                    onClick={() => setChatWidgets((prev) => prev.map((item) => item.id === widget.id ? { ...item, open: true, unreadCount: 0 } : item))}
                    title={widget.targetContactName || widget.threadTitle}
                  >
                    {(widget.targetContactName || widget.threadTitle).slice(0, 2).toUpperCase()}
                  </WidgetBubble>
                  {widget.unreadCount > 0 && <WidgetUnreadBadge>{widget.unreadCount > 9 ? '9+' : widget.unreadCount}</WidgetUnreadBadge>}
                  <WidgetPresenceDot $live={widget.live} />
                  <WidgetSoundIndicator $enabled={widget.soundEnabled}>
                    <Icon name="Bell" size={11} />
                  </WidgetSoundIndicator>
                </WidgetBubbleWrap>
              ))}
            </WidgetRail>
          </>
        )}
      </RoleProvider>
    </PreferencesProvider>
  );
}

export default App;
